import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupOffIcon from '@mui/icons-material/GroupRemove';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import CompetenciaSelector, {
  formatCompetencia,
  formatCompetenciaLonga,
  getCompetenciaInicial,
  saveCompetencia,
} from '../../components/saude/CompetenciaSelector';

/** Tipos de equipe da Atencao Basica (nu_ms do CNES). */
const TIPOS = [
  { value: '70', label: 'eSF' },
  { value: '71', label: 'eSB' },
  { value: '72', label: 'eMulti' },
  { value: '73', label: 'eCR' },
  { value: '74', label: 'eAPP' },
  { value: '76', label: 'eAP' },
];

const ROTULO_SITUACAO: Record<string, { label: string; color: 'error' | 'warning' | 'success' }> = {
  parou: { label: 'Parou de produzir', color: 'error' },
  nunca_produziu: { label: 'Nunca produziu', color: 'warning' },
  ok: { label: 'Com producao', color: 'success' },
};

const num = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');
const formatDateBr = (iso?: string | null): string =>
  iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : '-';

interface KpiProps {
  icon: React.ReactNode;
  label: string;
  valor: React.ReactNode;
  cor: string;
  hint?: string;
}

const Kpi: React.FC<KpiProps> = ({ icon, label, valor, cor, hint }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
      <CardContent sx={{ textAlign: 'center' }}>
        <Box sx={{ color: cor }}>{icon}</Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
          {hint && (
            <Tooltip title={hint}>
              <InfoOutlinedIcon sx={{ fontSize: 13, ml: 0.4, verticalAlign: 'middle', opacity: 0.6 }} />
            </Tooltip>
          )}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ color: cor }}>
          {valor}
        </Typography>
      </CardContent>
    </Card>
  );
};

/**
 * Relatorio "Equipes sem Producao" — item de licitacao "verificacao se tem alguma equipe
 * zerada no final do mes".
 *
 * Mostra, para uma competencia MM/AAAA, quais equipes ATIVAS da Atencao Basica nao registraram
 * nenhum tipo de producao. Separa quem "parou de produzir" (produzia e zerou — o caso
 * acionavel) de quem "nunca produziu" (INE credenciado que ainda nao entrou em operacao).
 */
const SaudeEquipesSemProducao: React.FC = () => {
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [competencia, setCompetencia] = useState(getCompetenciaInicial());
  const [tipos, setTipos] = useState<string[]>(TIPOS.map((t) => t.value));

  const unidadeParam = unidadeSelecionada?.codunidade;
  const tiposParam = useMemo(
    () => (tipos.length > 0 && tipos.length < TIPOS.length ? tipos.join(',') : undefined),
    [tipos],
  );

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    saudeService
      .getEquipesSemProducao(municipioAtivo.id, competencia, unidadeParam, tiposParam)
      .then((res: any) => setData(res))
      .catch((err: any) =>
        setError(err.response?.data?.message || 'Erro ao carregar a verificacao de equipes sem producao.'),
      )
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, competencia, unidadeParam, tiposParam]);

  const trocarCompetencia = useCallback((nova: string) => {
    setCompetencia(nova);
    saveCompetencia(nova);
  }, []);

  const exportarCsv = useCallback(() => {
    if (!municipioAtivo?.id) return;
    saudeService
      .exportarEquipesSemProducaoCsv(municipioAtivo.id, competencia, unidadeParam, tiposParam)
      .catch(() => setError('Falha ao exportar CSV.'));
  }, [municipioAtivo?.id, competencia, unidadeParam, tiposParam]);

  const exportarPdf = useCallback(() => {
    if (!municipioAtivo?.id) return;
    saudeService
      .exportarEquipesSemProducaoPdf(municipioAtivo.id, competencia, unidadeParam, tiposParam)
      .catch(() => setError('Falha ao exportar PDF.'));
  }, [municipioAtivo?.id, competencia, unidadeParam, tiposParam]);

  if (!municipioAtivo?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Selecione um municipio para acessar o relatorio.</Alert>
      </Box>
    );
  }

  const zeradas: any[] = (data?.equipes || []).filter((e: any) => e.situacao !== 'ok');
  const comProducao: any[] = (data?.equipes || []).filter((e: any) => e.situacao === 'ok');
  const apurado =
    !!data && data.dwh_implementado !== false && !data.competencia_sem_dados && !data.sem_equipes;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            <GroupOffIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#c62828' }} />
            Equipes sem Producao
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Equipes da Atencao Basica sem nenhum tipo de producao registrada na competencia
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadIcon />}
            onClick={exportarCsv}
            disabled={loading || !apurado || zeradas.length === 0}
          >
            CSV
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportarPdf}
            disabled={loading || !apurado}
          >
            PDF
          </Button>
          <CompetenciaSelector
            competencia={competencia}
            onChange={trocarCompetencia}
            maxCompetencia={data?.max_competencia}
            disabled={loading}
          />
        </Box>
      </Box>

      {/* Escopo e filtro por tipo de equipe */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Chip
          icon={<GroupsIcon sx={{ fontSize: 16 }} />}
          label={unidadeSelecionada ? unidadeSelecionada.nome : 'Todas as unidades do municipio'}
          variant="outlined"
          color={unidadeSelecionada ? 'default' : 'primary'}
        />
        <Typography variant="caption" color="text.secondary">
          Tipo de equipe:
        </Typography>
        <ToggleButtonGroup
          value={tipos}
          onChange={(_, novos: string[]) => {
            // Nunca deixar o filtro vazio: sem tipo nenhum nao ha o que apurar.
            if (novos && novos.length > 0) setTipos(novos);
          }}
          size="small"
        >
          {TIPOS.map((t) => (
            <ToggleButton
              key={t.value}
              value={t.value}
              sx={{ px: 1.2, py: 0.3, textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
            >
              {t.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : data?.dwh_implementado === false ? (
        <Alert severity="info">
          <AlertTitle>Nao disponivel para este municipio</AlertTitle>
          {data.motivo ||
            'Este municipio le os indicadores do Data Warehouse. A verificacao por competencia esta implementada apenas para conexao direta ao PEC.'}
        </Alert>
      ) : data?.competencia_sem_dados ? (
        <Alert severity="warning">
          <AlertTitle>Competencia sem dados</AlertTitle>
          Nenhuma producao foi registrada no municipio em{' '}
          <strong>{formatCompetenciaLonga(data.competencia)}</strong>, portanto nao e possivel dizer
          que uma equipe especifica esta zerada — o que falta e o processamento da competencia
          inteira.
          {data.max_competencia && (
            <>
              {' '}
              A ultima competencia com dados e{' '}
              <Button
                size="small"
                sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}
                onClick={() => trocarCompetencia(data.max_competencia)}
              >
                <strong>{formatCompetencia(data.max_competencia)}</strong>
              </Button>
              .
            </>
          )}
        </Alert>
      ) : data?.sem_equipes ? (
        <Alert severity="warning">
          <AlertTitle>Nenhuma equipe para apurar</AlertTitle>
          {data.motivo}
        </Alert>
      ) : data ? (
        <>
          {/* KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                icon={<GroupsIcon />}
                label="Equipes ativas"
                valor={num(data.total_ativas)}
                cor={theme.palette.text.primary}
                hint="Equipes da Atencao Basica ativas no CNES nos tipos e na unidade selecionados"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                icon={<GroupOffIcon />}
                label="Sem nenhuma producao"
                valor={num(data.total_zeradas)}
                cor={data.total_zeradas > 0 ? '#c62828' : '#2e7d32'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                icon={<TrendingDownIcon />}
                label="Pararam de produzir"
                valor={num(data.total_parou)}
                cor={data.total_parou > 0 ? '#c62828' : '#2e7d32'}
                hint="Equipes que registravam producao antes e zeraram nesta competencia"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Kpi
                icon={<HourglassEmptyIcon />}
                label="Nunca produziram"
                valor={num(data.total_nunca_produziu)}
                cor={data.total_nunca_produziu > 0 ? '#e65100' : '#2e7d32'}
                hint="INE credenciado que nunca registrou producao — normalmente equipe nova, ainda sem operacao"
              />
            </Grid>
          </Grid>

          {/* Competencia provavelmente ainda em processamento */}
          {data.competencia_parcial && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Competencia possivelmente incompleta</AlertTitle>
              {formatCompetenciaLonga(data.competencia)} tem{' '}
              <strong>{num(data.total_fichas)}</strong> registros contra{' '}
              <strong>{num(data.producao_competencia_anterior)}</strong> na competencia anterior. Um
              volume tao abaixo do habitual costuma indicar que a competencia ainda nao foi
              totalmente processada — as equipes abaixo podem estar apenas aguardando o envio dos
              dados, e nao paradas.
            </Alert>
          )}

          {/* Resultado */}
          {zeradas.length === 0 ? (
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
              <AlertTitle>Nenhuma equipe zerada em {formatCompetenciaLonga(data.competencia)}</AlertTitle>
              Todas as {num(data.total_ativas)} equipes ativas da Atencao Basica registraram producao na
              competencia.
            </Alert>
          ) : (
            <Paper
              elevation={0}
              sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 3, overflow: 'hidden' }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Equipes sem producao em {formatCompetenciaLonga(data.competencia)} ({zeradas.length})
                </Typography>
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>INE</TableCell>
                      <TableCell>Equipe</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Unidade</TableCell>
                      <TableCell>Situacao</TableCell>
                      <TableCell align="right">Ultima producao</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {zeradas.map((eq: any) => {
                      const sit = ROTULO_SITUACAO[eq.situacao] || ROTULO_SITUACAO.ok;
                      return (
                        <TableRow key={eq.ine} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{eq.ine}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{eq.equipe}</TableCell>
                          <TableCell>
                            <Chip label={eq.tipo_label} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{eq.unidade || '-'}</TableCell>
                          <TableCell>
                            <Chip label={sit.label} size="small" color={sit.color} />
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {formatDateBr(eq.ultima_producao)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Quebra por tipo de equipe */}
          {(data.por_tipo_equipe || []).length > 0 && (
            <Accordion elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Por tipo de equipe
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell align="right">Ativas</TableCell>
                        <TableCell align="right">Sem producao</TableCell>
                        <TableCell align="right">Pararam</TableCell>
                        <TableCell align="right">Nunca produziram</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.por_tipo_equipe.map((t: any) => (
                        <TableRow key={t.tipo} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{t.label}</TableCell>
                          <TableCell align="right">{num(t.ativas)}</TableCell>
                          <TableCell align="right" sx={{ color: t.zeradas > 0 ? '#c62828' : undefined, fontWeight: t.zeradas > 0 ? 700 : 400 }}>
                            {num(t.zeradas)}
                          </TableCell>
                          <TableCell align="right">{num(t.parou)}</TableCell>
                          <TableCell align="right">{num(t.nunca_produziu)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Composicao da producao da competencia */}
          {(data.por_tipo_ficha || []).length > 0 && (
            <Accordion elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Producao da competencia por tipo de registro ({num(data.total_fichas)} no total)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo de registro</TableCell>
                        <TableCell align="right">Registros</TableCell>
                        <TableCell align="right">Equipes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.por_tipo_ficha.map((t: any) => (
                        <TableRow key={t.tipo_ficha} hover>
                          <TableCell>{t.tipo_ficha}</TableCell>
                          <TableCell align="right">{num(t.fichas)}</TableCell>
                          <TableCell align="right">{num(t.equipes)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Equipes com producao (conferencia) */}
          {comProducao.length > 0 && (
            <Accordion elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Equipes com producao ({comProducao.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer sx={{ overflowX: 'auto', maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Equipe</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Unidade</TableCell>
                        <TableCell align="right">Registros</TableCell>
                        <TableCell align="right">Tipos de registro</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comProducao.map((eq: any) => (
                        <TableRow key={eq.ine} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{eq.equipe}</TableCell>
                          <TableCell>
                            <Chip label={eq.tipo_label} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{eq.unidade || '-'}</TableCell>
                          <TableCell align="right">{num(eq.fichas)}</TableCell>
                          <TableCell align="right">{num(eq.tipos_ficha)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Ressalvas */}
          {(data.limitacoes || []).length > 0 && (
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
              <AlertTitle sx={{ fontSize: '0.85rem' }}>Como este numero e apurado</AlertTitle>
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: '0.8rem', mb: 0.3 } }}>
                {data.limitacoes.map((l: string, i: number) => (
                  <li key={i}>{l}</li>
                ))}
              </Box>
            </Alert>
          )}
        </>
      ) : null}
    </Box>
  );
};

export default SaudeEquipesSemProducao;
